import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import api from "@/lib/api";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProductStatus } from "@/components/ProductStatus";
import { isUsableEmailAddress } from "@/lib/emailValidation";

const companyFields = tenant => ({
  name: tenant.name || "", company_name: tenant.company_name || "",
  vat_number: tenant.vat_number || "", tax_code: tenant.tax_code || "",
  sdi_code: tenant.sdi_code || "", pec_email: tenant.pec_email || "",
  address: tenant.address || "", cap: tenant.cap || "",
  city: tenant.city || "", country: tenant.country || "IT",
  phone: tenant.phone || "", order_email: tenant.order_email || "",
  notes: tenant.notes || "",
});

function Field({ label, value, onChange, type = "text", disabled = false, testid, autoComplete }) {
  return (
    <div>
      <label className="label-tiny" htmlFor={testid}>{label}</label>
      <input id={testid} type={type} value={value ?? ""} onChange={(e)=>onChange(e.target.value)}
             disabled={disabled} className="input-luxury mb-4"
             data-testid={testid} autoComplete={autoComplete} />
    </div>
  );
}

function CompanyTab() {
  const { user, isReadonly, subscriptionDisabled } = useAuth();
  const location = useLocation();
  const returnTo = /^\/suppliers\/[^/?#]+\?tab=ordini$/.test(location.state?.returnTo || "") ? location.state.returnTo : null;
  const focusedFromShortcut = useRef(false);
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orderEmailError, setOrderEmailError] = useState("");

  useEffect(() => {
    api.get("/me/tenant")
      .then((r) => {
        setTenant(r.data);
        setForm(companyFields(r.data));
        setBaseline(JSON.stringify(companyFields(r.data)));
      })
      .catch(()=> toast.error("Errore caricamento anagrafica"))
      .finally(()=> setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading && form && !focusedFromShortcut.current && window.location.hash === "#email-ordini") {
      focusedFromShortcut.current = true;
      requestAnimationFrame(() => document.getElementById("prof-order-email")?.focus());
    }
  }, [loading, form]);

  const save = async () => {
    if (!form) return false;
    const emailField = document.getElementById("prof-order-email");
    if (form.order_email.trim() && (!emailField?.checkValidity() || !isUsableEmailAddress(form.order_email))) {
      setOrderEmailError("Inserisci un indirizzo email ordini valido");
      emailField?.focus();
      return false;
    }
    setOrderEmailError("");
    setSaving(true);
    try {
      const r = await api.put("/me/tenant", { ...form, order_email: form.order_email.trim() || null });
      setTenant(r.data);
      const saved = { ...form, order_email: r.data.order_email || "" };
      setForm(saved);
      setBaseline(JSON.stringify(saved));
      toast.success("Dati societari aggiornati");
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 422) {
        setOrderEmailError("Inserisci un indirizzo email ordini valido");
        emailField?.focus();
      } else toast.error(typeof detail === "string" ? detail : "Errore salvataggio");
      return false;
    } finally { setSaving(false); }
  };

  const dirty = !isReadonly && !!form && baseline !== null && baseline !== JSON.stringify(form);
  useUnsavedChanges(dirty, save);

  if (loading) return <div className="text-sm text-[#707070]">Caricamento...</div>;
  if (!form) return null;

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const disabled = isReadonly;
  const savedOrderEmail = (tenant?.order_email || "").trim();
  const emailChanged = form.order_email.trim() !== savedOrderEmail;
  const orderEmailStatus = emailChanged
    ? (form.order_email.trim() && !isUsableEmailAddress(form.order_email) ? "Non valida" : "Da salvare")
    : !savedOrderEmail ? "Da configurare" : isUsableEmailAddress(savedOrderEmail) ? "Configurata" : "Da correggere";

  return (
    <div className="workflow-ux mt-6" data-testid="company-tab">
      {isReadonly && !subscriptionDisabled && (
        <div className="mb-6 px-4 py-3 rounded bg-[#F5F2EC] border border-[#E5E5E0] text-xs text-[#8A8579]">
          Il tuo ruolo è <strong>sola lettura</strong>: puoi consultare l&apos;anagrafica ma non modificarla.
        </div>
      )}

      <div className="text-[10px] tracking-luxury text-[#8A8579] uppercase mb-4">Ragione sociale</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <Field label="Nome abbonamento" value={form.name} onChange={set("name")} disabled={disabled} testid="prof-name" />
        <Field label="Ragione sociale" value={form.company_name} onChange={set("company_name")} disabled={disabled} testid="prof-company" />
        <Field label="P. IVA" value={form.vat_number} onChange={set("vat_number")} disabled={disabled} testid="prof-vat" />
        <Field label="Codice fiscale" value={form.tax_code} onChange={set("tax_code")} disabled={disabled} testid="prof-tax" />
        <Field label="Codice SDI" value={form.sdi_code} onChange={set("sdi_code")} disabled={disabled} testid="prof-sdi" />
        <Field label="PEC" value={form.pec_email} onChange={set("pec_email")} type="email" disabled={disabled} testid="prof-pec" />
      </div>

      <div className="text-[10px] tracking-luxury text-[#8A8579] uppercase mt-6 mb-4">Sede</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <Field label="Indirizzo" value={form.address} onChange={set("address")} disabled={disabled} testid="prof-address" />
        <Field label="CAP" value={form.cap} onChange={set("cap")} disabled={disabled} testid="prof-cap" />
        <Field label="Città" value={form.city} onChange={set("city")} disabled={disabled} testid="prof-city" />
        <Field label="Paese" value={form.country} onChange={set("country")} disabled={disabled} testid="prof-country" />
      </div>

      <div className="text-[10px] tracking-luxury text-[#8A8579] uppercase mt-6 mb-4">Contatti</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div><span className="label-tiny">Email amministrativa</span>
          <p className="text-sm py-2 break-all" data-testid="prof-email">{tenant?.email || "—"}</p></div>
        <div id="email-ordini" className="scroll-mt-20 mb-4" data-testid="prof-order-email-field">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <label className="label-tiny !mb-0" htmlFor="prof-order-email">Email ordini ai fornitori</label>
            <ProductStatus tone={orderEmailStatus === "Configurata" ? "success" : "warning"}
                           testId="prof-order-email-status">
              {orderEmailStatus}
            </ProductStatus>
          </div>
          <input id="prof-order-email" type="email" value={form.order_email}
                 onChange={e => { set("order_email")(e.target.value); setOrderEmailError(""); }}
                 placeholder="ordini@azienda.it" autoComplete="email" disabled={disabled}
                 aria-invalid={!!orderEmailError} aria-describedby={`prof-order-email-help${orderEmailError ? " prof-order-email-error" : ""}`}
                 className="input-luxury" data-testid="prof-order-email" />
          {orderEmailError && <p id="prof-order-email-error" className="text-sm text-red-700 mt-1" role="alert"
                                 data-testid="prof-order-email-error">{orderEmailError}</p>}
          <p id="prof-order-email-help" className="text-xs text-[#707070] mt-2" data-testid="prof-order-email-help">
            Per le risposte dei fornitori, il testo e il PDF degli ordini. Non cambia l’email di accesso.
          </p>
          {returnTo && <Link to={returnTo} className="inline-block text-xs underline mt-2" data-testid="prof-return-to-orders">
            Torna agli ordini del fornitore
          </Link>}
        </div>
        <Field label="Telefono" value={form.phone} onChange={set("phone")} disabled={disabled} testid="prof-phone" />
      </div>

      <div>
        <label className="label-tiny">Note</label>
        <textarea value={form.notes} onChange={(e)=>set("notes")(e.target.value)}
                  disabled={disabled} className="input-luxury mb-6 min-h-[100px]"
                  data-testid="prof-notes" />
      </div>

      <div className="text-[10px] tracking-luxury text-[#8A8579] uppercase mb-2">Abbonamento</div>
      <div className="text-xs text-[#707070] mb-6">
        Piano: <strong className="text-[#1A1A1A]">{tenant?.plan || "standard"}</strong>
        {" · "}Stato: <strong className="text-[#1A1A1A]">{tenant?.status || "-"}</strong>
        {tenant?.subscription_end && (
          <> · Scadenza: <strong className="text-[#1A1A1A]">
            {new Date(tenant.subscription_end).toLocaleDateString("it-IT")}
          </strong></>
        )}
        <div className="mt-1 text-[11px] text-[#8A8579]">
          Il rinnovo e lo stato sono gestiti dal Superadmin.
        </div>
      </div>

      {!disabled && (
        <div className="flex justify-end">
          <button onClick={save} disabled={saving}
                  data-testid="save-company"
                  className="btn-primary">
            {saving ? "Salvataggio..." : "Salva modifiche"}
          </button>
        </div>
      )}
    </div>
  );
}

function PasswordTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("La nuova password deve avere almeno 6 caratteri"); return; }
    if (next !== confirm) { toast.error("Le due password non coincidono"); return; }
    setSaving(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento password");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mt-6 max-w-md" data-testid="password-tab">
      <div className="text-[10px] tracking-luxury text-[#8A8579] uppercase mb-4">Cambio password</div>
      <p className="text-xs text-[#707070] mb-6">
        Scegli una password di almeno 6 caratteri. Per motivi di sicurezza serve la password attuale.
      </p>

      <label className="label-tiny">Password attuale</label>
      <input type="password" value={current} onChange={(e)=>setCurrent(e.target.value)} required
             className="input-luxury mb-5" data-testid="pwd-current"
             autoComplete="current-password" />

      <label className="label-tiny">Nuova password</label>
      <input type="password" value={next} onChange={(e)=>setNext(e.target.value)} required minLength={6}
             className="input-luxury mb-5" data-testid="pwd-new"
             autoComplete="new-password" />

      <label className="label-tiny">Conferma nuova password</label>
      <input type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} required minLength={6}
             className="input-luxury mb-8" data-testid="pwd-confirm"
             autoComplete="new-password" />

      <button type="submit" disabled={saving} className="btn-primary" data-testid="save-password">
        {saving ? "Salvataggio..." : "Aggiorna password"}
      </button>
    </form>
  );
}

export default function Profile() {
  const { user, subscriptionDisabled, isReadonly } = useAuth();
  return (
    <div className="p-8 md:p-12 max-w-4xl" data-testid="profile-page">
      <div className="text-[10px] tracking-luxury text-[#8A8579] uppercase mb-3">Il tuo profilo</div>
      <h1 className="font-serif text-4xl text-[#1A1A1A] mb-1">Anagrafica abbonato</h1>
      <div className="text-sm text-[#707070] mb-8">{user?.email}</div>

      <Tabs defaultValue="company">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="company" data-testid="tab-company">Dati societari</TabsTrigger>
          {!subscriptionDisabled && !isReadonly && <TabsTrigger value="password" data-testid="tab-password">Cambia password</TabsTrigger>}
        </TabsList>
        <TabsContent value="company"><CompanyTab /></TabsContent>
        {!subscriptionDisabled && !isReadonly && <TabsContent value="password"><PasswordTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
